import "./AnnualInterview.scss";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "core/buttons/button/Button";
import {
  ANNUAL_BALANCESHEET_YEARS,
  BALANCESHEET_STATUS,
  INTERVIEW_STATUS,
} from "constants/constants";
import { Typography } from "core/typography/Typography";
import { FormattedMessage } from "react-intl";
import { useAnnualBalancesheetContext } from "api/balance-sheet/annual/AnnualBalancesheetProvider";
import PropTypes from "prop-types";
import { notify } from "utils/functions";

const REMINDER_TIMEOUT_MS = 8 * 1000; // an 8-second wait

const AnnualInterview = ({
  consultantBySlug,
  userScope,
  personEvaluations,
}) => {
  const navigate = useNavigate();
  const { sendAnnualEvaluationReminder } = useAnnualBalancesheetContext();
  const reminderTimeoutsRef = useRef({});
  const [disabledReminderIds, setDisabledReminderIds] = useState({});

  useEffect(
    () => () => {
      Object.values(reminderTimeoutsRef.current).forEach((timeoutId) =>
        clearTimeout(timeoutId)
      );
    },
    []
  );

  const setReminderDisabled = (evaluationId, disabled) => {
    setDisabledReminderIds((prev) => {
      const next = { ...prev };
      if (disabled) {
        next[evaluationId] = true;
      } else {
        delete next[evaluationId];
      }
      return next;
    });
  };

  const handleSendReminder = async (evalData) => {
    const evaluationId = String(evalData.evaluation_id);

    if (disabledReminderIds[evaluationId]) return;

    setReminderDisabled(evaluationId, true);

    try {
      const reminderResult = await sendAnnualEvaluationReminder({
        person_id: evalData.person_id,
        evaluation_id: evalData.evaluation_id,
      });
      if (reminderResult === null) {
        setReminderDisabled(evaluationId, false);
        return;
      }

      notify("Un mail de rappel a été envoyé.");

      clearTimeout(reminderTimeoutsRef.current[evaluationId]);
      reminderTimeoutsRef.current[evaluationId] = setTimeout(() => {
        setReminderDisabled(evaluationId, false);
        delete reminderTimeoutsRef.current[evaluationId];
      }, REMINDER_TIMEOUT_MS);
    } catch (e) {
      setReminderDisabled(evaluationId, false);
    }
  };

  const handleNavigateToEval = (year) => {
    const evalObj = personEvaluations?.person_evaluations.find(
      (evaluation) => evaluation.year === year
    );
    if (evalObj) {
      navigate(
        `/${userScope}/evaluation/professional-evaluation/show/consultant/` +
          `${evalObj.person_id}/${consultantBySlug?.slug}/${evalObj.year}?evaluation_id=${evalObj.evaluation_id}`
      );
    }
  };

  const currentYear = new Date().getFullYear();
  const startYear =
    currentYear - ANNUAL_BALANCESHEET_YEARS.NUMBER_OF_PREVIOUS_YEARS_TO_SHOW;
  const endYear =
    currentYear + ANNUAL_BALANCESHEET_YEARS.NUMBER_OF_NEXT_YEARS_TO_SHOW;

  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, index) => startYear + index
  );

  const historyButtons = years.map((year) => {
    const evalData = personEvaluations?.person_evaluations?.find(
      (evaluation) => evaluation.year === year.toString()
    );
    const evaluationId = evalData?.evaluation_id
      ? String(evalData.evaluation_id)
      : null;
    const isReminderDisabled = evaluationId
      ? Boolean(disabledReminderIds[evaluationId])
      : false;

    let button = {
      id: year.toString(),
      label: year.toString(),
      status: INTERVIEW_STATUS.DISABLED,
      action: () => {},
      disabled: false,
    };

    if (evalData) {
      switch (evalData.status) {
        case BALANCESHEET_STATUS.PUBLISHED:
          button.status = INTERVIEW_STATUS.ACTIVE;
          button.action = () => handleNavigateToEval(year.toString());
          break;
        case BALANCESHEET_STATUS.PENDING:
        case BALANCESHEET_STATUS.CREATED:
          button.status = INTERVIEW_STATUS.ENABLED;
          button.action = () => handleNavigateToEval(year.toString());
          break;
        case BALANCESHEET_STATUS.CONSULTANT_PENDING:
        case BALANCESHEET_STATUS.CONSULTANT_UNFINISHED:
        case BALANCESHEET_STATUS.CONSULTANT_CREATED:
          button.status = INTERVIEW_STATUS.ENABLED;
          button.label += isReminderDisabled
            ? " - Mail de rappel envoye"
            : " - Envoyer un mail de rappel";
          button.action = () => {
            handleSendReminder(evalData);
          };
          button.isMailReminder = true;
          button.color = "pink";
          button.disabled = isReminderDisabled;
          break;
        default:
          button.status = INTERVIEW_STATUS.DISABLED;
          break;
      }
    }

    return button;
  });
  return (
    <div className="section-n">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="annual_interview_title"
            defaultMessage="Entretiens professionnels annuels online"
          />
        }
      />
      <Typography
        className="title-4 light-slate-gray"
        type="h4"
        label={
          <FormattedMessage
            id="annual_interview_subtitle"
            defaultMessage="Historique des entretiens professionnels annuels"
          />
        }
      />
      <div className="training-section">
        {historyButtons.map((button, index) => (
          <Button
            key={`${button.id}-${index}`}
            id={button.id}
            className={`btn-standard btn-outline ${
              button.isMailReminder ? "btn-reminder" : ""
            }`}
            type="button"
            disabled={
              button.disabled || button.status === INTERVIEW_STATUS.DISABLED
            }
            onClick={button.action}
            color={button.color}
          >
            {button.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

AnnualInterview.propTypes = {
  consultantBySlug: PropTypes.shape({
    slug: PropTypes.string,
  }),
  userScope: PropTypes.string,
  personEvaluations: PropTypes.shape({
    person_evaluations: PropTypes.arrayOf(PropTypes.shape({
      year: PropTypes.string,
      person_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      evaluation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
    })),
  }),
};

export default AnnualInterview;
